import * as z from 'zod';
import { Timestamp, FieldValue } from '@angular/fire/firestore';

/**
 * Converts a `Date` or `Timestamp`, to
 * an `Timestamp`.
 */
export const TimestampDateToTimestampAngularSchema = z
  .union([z.date(), z.instanceof(Timestamp)])
  .transform((val) => (val instanceof Date ? Timestamp.fromDate(val) : val));

/** @alias `Timestamp` */
export type TimestampDateToTimestampAngular = z.infer<
  typeof TimestampDateToTimestampAngularSchema
>;

/**
 * Converts a `Date`, `Timestamp` or `null`, to
 * an `Timestamp` or `null`.
 */
export const TimestampDateNullToTimestampNullAngularSchema = z
  .union([z.date(), z.instanceof(Timestamp), z.null()])
  .transform((val) =>
    val === null ? null : val instanceof Date ? Timestamp.fromDate(val) : val,
  );

/** @alias `Timestamp | null` */
export type TimestampDateNullToTimestampAngular = z.infer<
  typeof TimestampDateNullToTimestampNullAngularSchema
>;

/**
 * Converts a `Date`, `Timestamp`, or
 * `FieldValue` to an `Timestamp`
 * or `FieldValue`.
 */
export const FieldValueDateToTimestampFieldValueAngularSchema = z
  .union([z.date(), z.custom<FieldValue>()])
  .transform((val) => (val instanceof Date ? Timestamp.fromDate(val) : val));

/** @alias `Timestamp | FieldValue` */
export type FieldValueDateToTimestampFieldValueAngular = z.infer<
  typeof FieldValueDateToTimestampFieldValueAngularSchema
>;

/**
 * Converts a `Date`, `Timestamp` or
 * `FieldValue`, to an `Timestamp`
 * or `FieldValue`.
 */
export const TimestampDateFieldValueToTimestampFieldValueAngularSchema = z
  .union([z.date(), z.instanceof(Timestamp), z.custom<FieldValue>()])
  .transform((val) => {
    if (val instanceof Date) {
      return Timestamp.fromDate(val);
    } else if (val instanceof Timestamp) {
      return val;
    } else {
      return val;
    }
  });

/** @alias `Timestamp | FieldValue` */
export type TimestampDateFieldValueToTimestampAngular = z.infer<
  typeof TimestampDateFieldValueToTimestampFieldValueAngularSchema
>;

/**
 * Converts a `Date`, `Timestamp`,
 * `FieldValue`, or `null` to an `Timestamp`
 * or `FieldValue` or `null`.
 */
export const TimestampDateFieldValueNullToTimestampFieldValueNullAngularSchema =
  z
    .union([
      z.date(),
      z.instanceof(Timestamp),
      z.custom<FieldValue>(),
      z.null(),
    ])
    .transform((val) => {
      if (val === null) {
        return null;
      } else if (val instanceof Date) {
        return Timestamp.fromDate(val);
      } else if (val instanceof Timestamp) {
        return val;
      } else {
        return val;
      }
    });

/** @alias `FieldValue | Timestamp | null` */
export type TimestampDateFieldValueNullToTimestampFieldValueNullNode = z.infer<
  typeof TimestampDateFieldValueNullToTimestampFieldValueNullAngularSchema
>;
